import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Store } from '@ngrx/store';

import { deleteField, Firestore } from '@angular/fire/firestore';
import moment from 'moment';
import { UserModel } from '../../../models/user.model';
import * as userActions from '../../../state/actions/user.actions';
import { AppState } from '../../../state/appstate';
import { BtnBackComponent } from '../../components/buttons/btn-back/btn-back.component';
import { BtnSubmitComponent } from '../../components/buttons/btn-submit/btn-submit.component';
import { ContactComponent } from '../../components/dropdowns/contact/contact.component';
import { CountryComponent } from '../../components/dropdowns/country/country.component';
import { GenderComponent } from '../../components/dropdowns/gender/gender.component';

import { CheckboxComponent } from '../../components/inputs/checkbox/checkbox.component';

@Component({
  selector: 'app-edit-profile',
  standalone: true,
  imports: [
    CommonModule,
    BtnSubmitComponent,
    BtnBackComponent,
    ReactiveFormsModule,
    GenderComponent,
    CountryComponent,
    ContactComponent,
    CheckboxComponent,
  ],
  templateUrl: './edit-profile.component.html',
  styleUrls: ['./edit-profile.component.css'],
})
export class EditProfileComponent {
  private readonly afs: Firestore = inject(Firestore);
  user: UserModel | null = null;

  today: Date = new Date();

  pageForm: FormGroup;
  working: boolean = false;

  constructor(private store: Store<AppState>, private fb: FormBuilder) {
    this.pageForm = this.fb.group({
      name: new FormControl('', [Validators.required]),
      profession: new FormControl(''),
      contact: new FormControl(),
      dob: new FormControl(),
      isDobViewable: new FormControl(true),
      gender: new FormControl('unspecified'),
      buildingNo: new FormControl(''),
      buildingName: new FormControl(''),
      addressLine1: new FormControl(''),
      addressLine2: new FormControl(''),
      city: new FormControl('', [Validators.required]),
      country: new FormControl('', [Validators.required]),
      location: new FormControl(''),
      useUserCreditsFirst: new FormControl(false),
    });
    this.store.select('user').subscribe((userState) => {
      this.user = userState.user;
      this.working = userState.loading;
      this.pageForm.patchValue({
        name: this.user?.name,
        profession: this.user?.profession,
        contact: {
          isoCode: this.user?.contact?.isoCode,
          phoneCode: this.user?.contact?.phoneCode,
          contact: this.user?.contact?.contact,
        },
        dob: this.user?.dob
          ? moment(this.user?.dob).format('YYYY-MM-DD')
          : null,
        isDobViewable: this.user?.isDobViewable ?? true,
        gender: this.user?.gender ?? 'unspecified',
        buildingNo: this.user?.address?.buildingNo,
        buildingName: this.user?.address?.buildingName,
        addressLine1: this.user?.address?.addressLine1,
        addressLine2: this.user?.address?.addressLine2,
        city: this.user?.address?.city,
        country: this.user?.address?.country,
        location: this.user?.location,
        useUserCreditsFirst: this.user?.useUserCreditsFirst ?? false,
      });
    });
  }

  update() {
    if (this.pageForm.invalid) return;

    let temp: any = {
      uid: this.user?.uid,
      name: this.pageForm.get('name')?.value,
      profession: this.pageForm.get('profession')?.value ?? deleteField(),
      isDobViewable: this.pageForm.get('isDobViewable')?.value ?? true,
      gender: this.pageForm.get('gender')?.value ?? 'unspecified',
      address: {
        city: this.pageForm.get('city')?.value,
        country: this.pageForm.get('country')?.value,
      },
      useUserCreditsFirst: this.pageForm.get('useUserCreditsFirst')?.value ?? false,
    };

    if (this.pageForm.get('buildingNo')?.value) {
      temp.address.buildingNo = this.pageForm.get('buildingNo')?.value;
    }

    if (this.pageForm.get('buildingName')?.value) {
      temp.address.buildingName = this.pageForm.get('buildingName')?.value;
    }

    if (this.pageForm.get('addressLine1')?.value) {
      temp.address.addressLine1 = this.pageForm.get('addressLine1')?.value;
    }

    if (this.pageForm.get('addressLine2')?.value) {
      temp.address.addressLine2 = this.pageForm.get('addressLine2')?.value;
    }

    const isDobViewable = this.pageForm.get('isDobViewable')?.value ?? true;
    const dobVal = this.pageForm.get('dob')?.value;
    if (dobVal != null && isDobViewable) {
      temp.dob = moment(dobVal).toDate();
    } else {
      temp.dob = dobVal ? moment(dobVal).toDate() : null;
    }

    // console.log(`Contact: ${JSON.stringify(this.pageForm.value.contact)}`);
    if (
      this.pageForm.value.contact?.isoCode &&
      this.pageForm.value.contact?.isoCode !== '' &&
      this.pageForm.value.contact?.phoneCode &&
      this.pageForm.value.contact?.phoneCode !== '' &&
      this.pageForm.value.contact?.contact &&
      this.pageForm.value.contact?.contact !== ''
    ) {
      temp.contact = this.pageForm.get('contact')?.value;
    } else {
      temp.contact = deleteField();
    }

    this.store.dispatch(userActions.update({ user: temp }));
  }
}
